import type { PaigeEmailIntake, PaigeJobProgress, PaigeRetention } from './paige.global';

/** Whether an engine origin is a job's channel (`/v1/jobs/{id}`) rather than the tenant's (`/v1`). */
export function isJobChannel(engineOrigin: string): boolean {
  return /\/jobs\/[^/]+$/.test(engineOrigin);
}

/** The headers every call on the job's channel carries. */
function headers(tenant: string | undefined, json = false): Record<string, string> {
  return {
    ...(json ? { 'content-type': 'application/json' } : {}),
    ...(tenant ? { 'Paige-Tenant': tenant } : {}),
  };
}

/** The job's email source from `GET {engineOrigin}/sources`; null when the job has none or the call fails. */
export async function fetchEmailIntake(
  engineOrigin: string,
  tenant: string | undefined,
): Promise<PaigeEmailIntake | null> {
  const response = await fetch(`${engineOrigin}/sources`, {
    credentials: 'include',
    headers: headers(tenant),
  });
  if (!response.ok) return null;
  const sources = (await response.json()) as {
    kind: string;
    detail: string;
    health: PaigeEmailIntake['health'];
    lastActivityAt: string | null;
    holdReason?: unknown;
    enabled?: boolean;
  }[];
  const email = sources.find((s) => s.kind === 'email');
  if (!email) return null;
  return {
    address: email.detail,
    health: email.health,
    lastActivityAt: email.lastActivityAt,
    held: Boolean(email.holdReason),
    enabled: email.enabled !== false,
  };
}

/** What the mirror reads of the workbench's review policy (shared/desktop-api.ts, `ReviewPolicy`). */
export type MirroredReviewPolicy = {
  holdForReview?: boolean;
  fields?: number;
  processingMode?: 'straight-through' | 'ai-reviewed' | 'human-reviewed';
};

/**
 * Paige's automation mode for the workbench's policy. Job setup's Pilot mode is the processing mode,
 * two since CapturePoint eb76b26d: Autopilot (`ai-reviewed`, and the retired `straight-through`
 * read as it) has Cortexa's AI reviewer finish a doubtful document and nothing waits for a person,
 * which is Paige's automatic; Manual (`human-reviewed`) is supervised. A job saved before Pilot
 * mode existed says it with `holdForReview` alone.
 */
export function automationModeOf(policy: MirroredReviewPolicy): 'automatic' | 'supervised' {
  if (policy.processingMode)
    return policy.processingMode === 'human-reviewed' ? 'supervised' : 'automatic';
  return policy.holdForReview === false ? 'automatic' : 'supervised';
}

/**
 * The workbench's review policy, mirrored to Paige as the job's automation
 * (`PATCH {engineOrigin}/settings`): the Autopilot mode (`automationModeOf`), and the field
 * sensitivity as the confidence bar below which a document waits for a person. The engine's job
 * definition stays the truth for the workbench; Paige's copy is what its dashboard and API report.
 */
export async function mirrorReviewPolicy(
  engineOrigin: string,
  tenant: string | undefined,
  job: { reviewPolicy?: MirroredReviewPolicy | null },
): Promise<void> {
  if (!isJobChannel(engineOrigin)) return;
  const policy = job.reviewPolicy ?? {};
  const automation = {
    mode: automationModeOf(policy),
    ...(typeof policy.fields === 'number' ? { confidenceThreshold: policy.fields } : {}),
  };
  const response = await fetch(`${engineOrigin}/settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: headers(tenant, true),
    body: JSON.stringify({ automation }),
  });
  if (!response.ok)
    throw new Error('Paige could not save how this job is reviewed. Try again in a minute.');
}

/** The job's retention from `GET {engineOrigin}/settings`; null when the call fails. */
export async function fetchRetention(
  engineOrigin: string,
  tenant: string | undefined,
): Promise<PaigeRetention | null> {
  const response = await fetch(`${engineOrigin}/settings`, {
    credentials: 'include',
    headers: headers(tenant),
  });
  if (!response.ok) return null;
  const settings = (await response.json()) as { retention?: PaigeRetention };
  return settings.retention ?? 'export_30d';
}

/** Saves the job's retention through `PATCH {engineOrigin}/settings`. */
export async function saveRetention(
  engineOrigin: string,
  tenant: string | undefined,
  retention: PaigeRetention,
): Promise<void> {
  const response = await fetch(`${engineOrigin}/settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: headers(tenant, true),
    body: JSON.stringify({ retention }),
  });
  if (!response.ok)
    throw new Error('Paige could not save how long documents are kept. Try again in a minute.');
}

/**
 * Where the job's documents are, from its card (`GET {engineOrigin}/overview`): how many it holds,
 * how many are still in flight and how many pages are still being read. Null when the call fails.
 */
export async function fetchJobProgress(
  engineOrigin: string,
  tenant: string | undefined,
): Promise<PaigeJobProgress | null> {
  const response = await fetch(`${engineOrigin}/overview`, {
    credentials: 'include',
    headers: headers(tenant),
  });
  if (!response.ok) return null;
  const card = (await response.json()) as Partial<PaigeJobProgress>;
  return {
    documents: card.documents ?? 0,
    processing: card.processing ?? 0,
    pagesReading: card.pagesReading ?? 0,
  };
}
